// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import {
  Square,
  Circle,
  FileImage,
  TreePine,
  Building,
  Armchair,
  Car,
  Trash2,
  Droplets,
  Lightbulb,
  Fence,
  Info,
  DoorOpen,
  Waves,
  MousePointer,
  Hand,
  Pentagon,
  Type,
  MapPin,
  Edit3,
} from "lucide-react";
import { SVG_GROUP_TEMPLATE } from "./scenarioConfig";

// SVG optimization function (replaces svgo)
export const optimizeSVG = (svgString) => {
  try {
    return svgString
      .replace(/\s+/g, " ")
      .replace(/>\s+</g, "><")
      .replace(/\s*=\s*"/g, '="')
      .replace(/"\s+/g, '" ')
      .trim();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("SVG optimization failed:", error);
    return svgString;
  }
};

// Generate unique ID for SVG elements
export const generateId = () =>
  `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Calculate distance between two points
export const distance = (p1, p2) =>
  Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));

// Layer definitions
export const LAYERS = {
  BACKGROUND: "background",
  MAIN: "main",
  OVERLAY: "overlay",
};

export const LAYER_CONFIG = {
  [LAYERS.BACKGROUND]: {
    name: "Background",
    icon: FileImage,
    description: "Background images, maps, and floor plans",
    zIndex: 1,
  },
  [LAYERS.MAIN]: {
    name: "Main Objects",
    icon: Square,
    description: "Primary shapes and structures",
    zIndex: 2,
  },
  [LAYERS.OVERLAY]: {
    name: "Overlay",
    icon: TreePine,
    description: "Labels, annotations, and decorative elements",
    zIndex: 3,
  },
};

// Using centralized SVG group template from scenarioConfig
export const GROUP_TEMPLATE = SVG_GROUP_TEMPLATE;

// Initialize missing groups in SVG structure
export const initializeMissingGroups = (svgElement) => {
  if (!svgElement) return;
  const ns = "http://www.w3.org/2000/svg";

  // Find or create layer1 group
  let layer1 = svgElement.querySelector("g#layer1");
  if (!layer1) {
    layer1 = document.createElementNS(ns, "g");
    layer1.setAttribute("id", "layer1");
    svgElement.appendChild(layer1);
  }

  // Ensure each scenario->space pair exists
  GROUP_TEMPLATE.forEach(([scenarioId, spaceId]) => {
    let scenario = layer1.querySelector(`g#${scenarioId}`);
    if (!scenario) {
      scenario = document.createElementNS(ns, "g");
      scenario.setAttribute("id", scenarioId);
      layer1.appendChild(scenario);
    }

    if (!scenario.querySelector(`g#${spaceId}`)) {
      const space = document.createElementNS(ns, "g");
      space.setAttribute("id", spaceId);
      scenario.appendChild(space);
    }
  });
};

// Convert path data to polygon points
// Supports both SVG (x,y) and OSM (lng,lat) coordinate systems
export const pathToPolygon = (pathData, coordinateSystem = "svg") => {
  if (!pathData) return [];

  // Sample the real path geometry using the browser's native path API. This
  // handles every command type — curves (C/S/Q/T/A), relative commands (lowercase),
  // H/V — not just M/L, so curved/real-world SVG paths (Illustrator, maps, CAD
  // exports) convert into a proper polygon instead of falling back to a
  // "manual conversion needed" placeholder.
  let sampled = [];
  if (typeof document !== "undefined") {
    try {
      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttribute("d", pathData);
      const total = path.getTotalLength ? path.getTotalLength() : 0;
      if (total > 0) {
        // ~1 vertex per 8 user-units, clamped so large areas get enough detail
        // and tiny ones don't explode the vertex count.
        const steps = Math.min(256, Math.max(12, Math.round(total / 8)));
        for (let i = 0; i <= steps; i++) {
          const pt = path.getPointAtLength((total * i) / steps);
          sampled.push({ x: pt.x, y: pt.y });
        }
        // Drop a trailing vertex that duplicates the first (closed path).
        if (sampled.length > 1) {
          const first = sampled[0];
          const last = sampled[sampled.length - 1];
          if (Math.hypot(first.x - last.x, first.y - last.y) < 0.01) {
            sampled.pop();
          }
        }
      }
    } catch (e) {
      sampled = [];
    }
  }

  if (sampled.length === 0) {
    // Fallback (no DOM / unparseable): naive literal M/L coordinate parse.
    const commands = pathData.match(/[ML][\s\d.,-]+/gi) || [];
    commands.forEach((cmd) => {
      const coords = cmd
        .substring(1)
        .trim()
        .split(/[\s,]+/)
        .map(Number)
        .filter((n) => !isNaN(n));
      for (let i = 0; i < coords.length; i += 2) {
        if (coords[i] !== undefined && coords[i + 1] !== undefined) {
          sampled.push({ x: coords[i], y: coords[i + 1] });
        }
      }
    });
  }

  if (coordinateSystem === "osm") {
    // For OSM: first coord is lng, second is lat.
    return sampled.map((p) => ({ lng: p.x, lat: p.y }));
  }
  return sampled;
};

// Convert polygon points to path data
// Handles both SVG (x,y) and OSM (lng,lat) coordinate systems
export const polygonPointsToPath = (points, coordinateSystem = "svg") => {
  if (!points || points.length === 0) return "";
  if (coordinateSystem === "osm") {
    // For OSM: use lng,lat
    return `M ${points.map((p) => `${p.lng},${p.lat}`).join(" L ")} Z`;
  } else {
    // For SVG: use x,y
    return `M ${points.map((p) => `${p.x},${p.y}`).join(" L ")} Z`;
  }
};

// Park objects definition
export const PARK_OBJECTS = {
  tree: {
    icon: TreePine,
    label: "Tree/Shrub",
    defaultLayer: LAYERS.OVERLAY,
    fill: "#4caf50",
    stroke: "#2e7d32",
    geometry: {
      radius: 18,
      trunkWidth: 6,
      trunkHeight: 22,
      labelOffset: 28,
    },
  },
  stage: {
    icon: Building,
    label: "Stage/Platform",
    defaultLayer: LAYERS.MAIN,
    fill: "#8d6e63",
    stroke: "#5d4e37",
  },
  seating: {
    icon: Armchair,
    label: "Seating/Bench",
    defaultLayer: LAYERS.MAIN,
    fill: "#6d4c41",
    stroke: "#3e2723",
  },
  parking: {
    icon: Car,
    label: "Parking Lot",
    defaultLayer: LAYERS.BACKGROUND,
    fill: "#9e9e9e",
    stroke: "#424242",
  },
  toilet: {
    icon: Building,
    label: "Toilet/Restroom",
    defaultLayer: LAYERS.MAIN,
    fill: "#2196f3",
    stroke: "#0d47a1",
  },
  trash: {
    icon: Trash2,
    label: "Trash/Recycling",
    defaultLayer: LAYERS.OVERLAY,
    fill: "#ff9800",
    stroke: "#e65100",
    geometry: {
      radius: 18,
      trashWidth: 12,
      trashHeight: 18,
      labelOffset: 26,
    },
  },
  fountain_drink: {
    icon: Droplets,
    label: "Drinking Fountain",
    defaultLayer: LAYERS.OVERLAY,
    fill: "#00bcd4",
    stroke: "#006064",
    geometry: {
      radius: 18,
      cupTopWidth: 20,
      cupBottomWidth: 12,
      cupHeight: 22,
      labelOffset: 26,
    },
  },
  fountain_water: {
    icon: Waves,
    label: "Water Feature",
    defaultLayer: LAYERS.MAIN,
    fill: "#03a9f4",
    stroke: "#01579b",
  },
  lighting: {
    icon: Lightbulb,
    label: "Lighting Fixture",
    defaultLayer: LAYERS.OVERLAY,
    fill: "#ffeb3b",
    stroke: "#f57f17",
    geometry: {
      radius: 18,
      poleWidth: 3,
      poleHeight: 26,
      lampRadius: 9,
      labelOffset: 30,
    },
  },
  fence: {
    icon: Fence,
    label: "Fence/Gate",
    defaultLayer: LAYERS.MAIN,
    fill: "#795548",
    stroke: "#3e2723",
  },
  info_board: {
    icon: Info,
    label: "Information Board",
    defaultLayer: LAYERS.OVERLAY,
    fill: "#9c27b0",
    stroke: "#4a148c",
    geometry: {
      radius: 18,
      boardWidth: 24,
      boardHeight: 16,
      labelOffset: 26,
    },
  },
  entrance: {
    icon: DoorOpen,
    label: "Access Point/Entrance",
    defaultLayer: LAYERS.MAIN,
    fill: "#4caf50",
    stroke: "#1b5e20",
  },
};

// Tool definitions
export const tools = [
  { id: "select", icon: MousePointer, label: "Select" },
  { id: "drag", icon: Hand, label: "Drag" },
  { id: "rectangle", icon: Square, label: "Rectangle" },
  { id: "circle", icon: Circle, label: "Circle" },
  { id: "polygon", icon: Pentagon, label: "Polygon" },
  { id: "text", icon: Type, label: "Text" },
  { id: "pin", icon: MapPin, label: "Pin" },
  { id: "edit", icon: Edit3, label: "Edit Points" },
];

// Canvas constants
export const CANVAS_WIDTH = 800;
export const CANVAS_HEIGHT = 600;
export const GRID_SIZE = 20;

// Default scenario viewBox (the drawing area shown for a fresh scenario and
// what "Reset to Default" restores). Larger than the legacy 0/0/800/600 so
// there's room to draw spaces comfortably.
export const DEFAULT_VIEWBOX = { x: 400, y: 250, width: 1600, height: 1000 };
